import {
  CobaltDropdown,
  CobaltDropdownItem,
} from '@cae/cobalt-react/components/dropdown';
import { CobaltSectionMessage } from '@cae/cobalt-react/components/section-message';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { CobaltToggletip } from '@cae/cobalt-react/components/toggletip';
import { CobaltIconInfoCircleSolid } from '@cae/cobalt-react/icons/solid/CobaltIconInfoCircleSolid';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { UnsavedChangesModal } from '@shared/components/unsaved-changes-modal/UnsavedChangesModal';
import * as React from 'react';

import type { EditCitizenshipPanelProps } from './EditCitizenshipPanel.types';

import { useEditCitizenshipPanel } from '../../hooks/use-edit-citizenship-panel/useEditCitizenshipPanel';
import { EditCitizenshipActionBar } from './edit-citizenship-action-bar/EditCitizenshipActionBar';
import styles from './EditCitizenshipPanel.module.css';

const EditCitizenshipPanel = React.memo(
  ({ onClose }: Readonly<EditCitizenshipPanelProps>): React.ReactElement => {
    const {
      formState,
      isMobile,
      isExitModalOpen,
      isDirty,
      isPending,
      isError,
      isProfileLoading,
      isProfileError,
      isProfileReady,
      nationalities,
      countries,
      isOptionsLoading,
      isOptionsError,
      citizenshipAnchorEl,
      residencyAnchorEl,
      handleFieldChange,
      handleSubmit,
      handleCloseAttempt,
      handleExit,
      handleKeepEditing,
      handleCitizenshipToggletipClick,
      handleCitizenshipToggletipClose,
      handleResidencyToggletipClick,
      handleResidencyToggletipClose,
    } = useEditCitizenshipPanel({ onClose });

    const isLoading = isPending || (isProfileLoading && !isProfileReady) || isOptionsLoading;

    return (
      <>
        <CobaltSidePanel
          isOpen
          showCloseButton
          surtitle="Personal information"
          title="Citizenship and residency"
          description="We use your citizenship and residency to comply with international export control requirements for training."
          onClose={handleCloseAttempt}
          actionBar={
            <EditCitizenshipActionBar
              isMobile={isMobile}
              isPending={isPending}
              isProfileReady={isProfileReady}
              isDirty={isDirty}
              onCloseAttempt={handleCloseAttempt}
              onSubmit={handleSubmit}
            />
          }
          data-heap-id="edit-citizenship-panel"
          size={isMobile ? 'large' : 'small'}
        >
          <div className={styles.form}>
            {isLoading && <ContentLoader />}
            {isProfileError && (
              <CobaltSectionMessage
                variant="danger"
                sectionMessageTitle={
                  <h3>
                    We couldn&apos;t load your profile. Please close and try
                    again.
                  </h3>
                }
                data-heap-id="profile-error-banner"
              />
            )}
            {isOptionsError && (
              <CobaltSectionMessage
                variant="danger"
                sectionMessageTitle={
                  <h3>
                    We couldn&apos;t load the available options. Please try
                    again.
                  </h3>
                }
                data-heap-id="options-error-banner"
              />
            )}
            {isError && (
              <CobaltSectionMessage
                variant="danger"
                sectionMessageTitle={
                  <h3>
                    We couldn&apos;t save your information. Please try again.
                  </h3>
                }
                data-heap-id="mutation-error-banner"
              />
            )}

            <div>
              <div className={styles.dropdownLabel}>
                <span id="profile-citizenship-last-granted-citizenship-label">
                  Last granted citizenship
                </span>
                <button
                  type="button"
                  data-heap-id="profile-citizenship-last-granted-citizenship-info-btn"
                  aria-label="Last granted citizenship information"
                  className={styles.tooltipTrigger}
                  onClick={handleCitizenshipToggletipClick}
                  disabled={isLoading}
                >
                  <CobaltIconInfoCircleSolid size="small" />
                </button>
                <CobaltToggletip
                  title="Last granted citizenship"
                  description="Citizenship is the recognition by a country of civil, political, and social rights which are not afforded to non-citizens."
                  anchorEl={citizenshipAnchorEl}
                  isOpen={Boolean(citizenshipAnchorEl)}
                  onToggle={handleCitizenshipToggletipClose}
                  position="bottom-start"
                  className={styles.editCitizenshipToggletip}
                >
                  <span />
                </CobaltToggletip>
              </div>
              <CobaltDropdown
                id="profile-citizenship-last-granted-citizenship"
                data-heap-id="profile-citizenship-last-granted-citizenship"
                aria-labelledby="profile-citizenship-last-granted-citizenship-label"
                placeholder="Select country"
                value={formState.lastGrantedCitizenship || undefined}
                onSelectionChange={value =>
                  handleFieldChange(
                    'lastGrantedCitizenship',
                    (value as string) ?? ''
                  )
                }
                clearSelection
                filterable
                disabled={isLoading}
              >
                {nationalities.map(n => (
                  <CobaltDropdownItem key={n.iso2Code} value={n.iso2Code}>
                    {n.name}
                  </CobaltDropdownItem>
                ))}
              </CobaltDropdown>
            </div>

            <div>
              <div className={styles.dropdownLabel}>
                <span id="profile-citizenship-current-permanent-residency-label">
                  Country of permanent residency
                </span>
                <button
                  type="button"
                  data-heap-id="profile-citizenship-current-permanent-residency-info-btn"
                  aria-label="Country of permanent residency information"
                  className={styles.tooltipTrigger}
                  onClick={handleResidencyToggletipClick}
                  disabled={isLoading}
                >
                  <CobaltIconInfoCircleSolid size="small" />
                </button>
                <CobaltToggletip
                  title="Country of permanent residency"
                  description="Person's resident status in a country granted by the local authority with the right to reside on a permanent basis."
                  anchorEl={residencyAnchorEl}
                  isOpen={Boolean(residencyAnchorEl)}
                  onToggle={handleResidencyToggletipClose}
                  position="bottom-start"
                  className={styles.editCitizenshipToggletip}
                >
                  <span />
                </CobaltToggletip>
              </div>
              <CobaltDropdown
                id="profile-citizenship-current-permanent-residency"
                data-heap-id="profile-citizenship-current-permanent-residency"
                aria-labelledby="profile-citizenship-current-permanent-residency-label"
                placeholder="Select country"
                value={formState.currentPermanentResidency || undefined}
                onSelectionChange={value =>
                  handleFieldChange(
                    'currentPermanentResidency',
                    (value as string) ?? ''
                  )
                }
                clearSelection
                filterable
                disabled={isLoading}
              >
                {countries.map(c => (
                  <CobaltDropdownItem key={c.iso2Code} value={c.iso2Code}>
                    {c.name}
                  </CobaltDropdownItem>
                ))}
              </CobaltDropdown>
            </div>
          </div>
        </CobaltSidePanel>

        <UnsavedChangesModal
          isOpen={isExitModalOpen}
          confirmLabel="Keep Editing"
          cancelLabel="Exit"
          onConfirm={handleKeepEditing}
          onCancel={handleExit}
        />
      </>
    );
  }
);

EditCitizenshipPanel.displayName = 'EditCitizenshipPanel';

export { EditCitizenshipPanel };
